import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// AIRLOCK dropzone.
//
// Accepts drag-and-drop or click-to-select file uploads, direct-uploads the
// file to ActiveStorage, then POSTs the signed_id and filename back to the
// AIRLOCK create endpoint. On success the server responds with a Turbo Stream
// that swaps the drop zone element (#airlock_chamber) for the live intake
// stepper -- or, failing that, redirects via JSON to the intake detail page.
//
// Mirrors the pattern in product_images_dropzone_controller but is tailored
// for single-file document intake instead of image swap.
export default class extends Controller {
  static targets = ["fileInput", "status"]
  static values = { url: String }

  connect() {
    this.dragCounter = 0

    this.boundDragEnter = this.handleDragEnter.bind(this)
    this.boundDragLeave = this.handleDragLeave.bind(this)
    this.boundDragOver  = this.handleDragOver.bind(this)
    this.boundDrop      = this.handleDrop.bind(this)

    this.element.addEventListener("dragenter", this.boundDragEnter)
    this.element.addEventListener("dragleave", this.boundDragLeave)
    this.element.addEventListener("dragover",  this.boundDragOver)
    this.element.addEventListener("drop",      this.boundDrop)

    // Click anywhere on the panel triggers the hidden file input -- but only
    // if the click did not originate on a label/input already wired up.
    this.element.addEventListener("click", this.handlePanelClick.bind(this))
  }

  disconnect() {
    this.element.removeEventListener("dragenter", this.boundDragEnter)
    this.element.removeEventListener("dragleave", this.boundDragLeave)
    this.element.removeEventListener("dragover",  this.boundDragOver)
    this.element.removeEventListener("drop",      this.boundDrop)
  }

  handlePanelClick(event) {
    // Labels and inputs handle their own click; ignore.
    if (event.target.closest("label") || event.target.closest("input")) return
    if (this.hasFileInputTarget) this.fileInputTarget.click()
  }

  handleDragEnter(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!event.dataTransfer || !event.dataTransfer.types.includes("Files")) return
    this.dragCounter++
    if (this.dragCounter === 1) this.setDragOver(true)
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dragCounter = Math.max(0, this.dragCounter - 1)
    if (this.dragCounter === 0) this.setDragOver(false)
  }

  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dragCounter = 0
    this.setDragOver(false)

    const files = event.dataTransfer?.files
    if (!files || files.length === 0) return
    this.uploadFile(files[0])
  }

  fileInputChanged(event) {
    const file = event.target.files?.[0]
    if (!file) return
    this.uploadFile(file)
  }

  setDragOver(on) {
    const cls = ["border-sapphire", "bg-sapphire/5"]
    if (on) {
      this.element.classList.add(...cls)
    } else {
      this.element.classList.remove(...cls)
    }
  }

  setStatus(text) {
    if (!this.hasStatusTarget) return
    if (text) {
      this.statusTarget.textContent = text
      this.statusTarget.classList.remove("hidden")
    } else {
      this.statusTarget.textContent = ""
      this.statusTarget.classList.add("hidden")
    }
  }

  uploadFile(file) {
    this.setStatus(`Uploading ${file.name}…`)

    const upload = new DirectUpload(file, this.directUploadUrl)
    upload.create((error, blob) => {
      if (error) {
        console.error("AIRLOCK upload error:", error)
        this.setStatus(`Upload failed: ${error}`)
        return
      }
      this.submitIntake(blob, file)
    })
  }

  submitIntake(blob, file) {
    this.setStatus(`Queuing ${file.name} for classification…`)

    const formData = new FormData()
    formData.append("airlock[file]",     blob.signed_id)
    formData.append("airlock[filename]", file.name)

    fetch(this.urlValue, {
      method:  "POST",
      body:    formData,
      headers: {
        "X-CSRF-Token": this.csrfToken,
        "Accept":       "text/vnd.turbo-stream.html, application/json"
      }
    })
      .then(response => {
        const contentType = response.headers.get("Content-Type") || ""
        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        if (contentType.includes("turbo-stream")) {
          return response.text().then(html => {
            window.Turbo.renderStreamMessage(html)
            this.setStatus(null)
          })
        }
        if (contentType.includes("application/json")) {
          return response.json().then(data => {
            if (data.url) window.Turbo.visit(data.url)
          })
        }
        // Location header fallback.
        const location = response.headers.get("Location")
        if (location) window.Turbo.visit(location)
      })
      .catch(err => {
        console.error("AIRLOCK submit error:", err)
        this.setStatus("Could not queue intake. Please try again.")
      })
  }

  get directUploadUrl() {
    return document.querySelector('meta[name="direct-upload-url"]')?.content || "/rails/active_storage/direct_uploads"
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
